/**
 * Take On-Call Modal
 * Quick action to take over current on-call duty
 * Uses existing reassignment API
 */

import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { Dialog, Button, FormItem, Typogaphy } from 'vnetwork-ui'

import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useAuthStore from '@/stores/useAuthStore'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

import {
    getCurrentOncallAssignments,
    reassignOncallAssignment,
} from '@/modules/Oncall/services'
import { OncallAssignmentResponse } from '@/modules/Oncall/types/response'

interface FormData {
    assignment_id: string
    reason: string
}

export interface TakeOncallModalProps {
    open: boolean
    onClose: () => void
    scheduleId?: number
    onConfirm?: () => void
}

const TakeOncallModal: React.FC<TakeOncallModalProps> = ({
    open,
    onClose,
    scheduleId,
    onConfirm,
}) => {
    const { t: translate } = useTranslation('oncall')
    const { employee } = useAuthStore()
    const [isProcessing, setIsProcessing] = useState(false)

    // Fetch current on-call assignments
    const { data: currentAssignments, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ONCALL_ASSIGNMENTS, 'current-for-take'],
        queryFn: getCurrentOncallAssignments,
        enabled: open,
    })

    // Filter by schedule if provided
    const availableAssignments = useMemo(() => {
        const assignments =
            currentAssignments?.results ||
            (currentAssignments as any)?.result ||
            []
        if (scheduleId) {
            return assignments.filter(
                (a: OncallAssignmentResponse) =>
                    a.oncall_schedule_id === scheduleId
            )
        }
        return assignments
    }, [currentAssignments, scheduleId])

    const assignmentOptions = useMemo(() => {
        return availableAssignments.map((a: OncallAssignmentResponse) => ({
            label: `${a.oncall_schedule_name} - ${a.employee_name} (${dayjs(a.oncall_date).format('DD/MM')})`,
            value: String(a.oncall_assignment_id),
        }))
    }, [availableAssignments])

    const { control, handleSubmit, reset, watch } = useForm<FormData>({
        mode: 'onSubmit',
        defaultValues: {
            assignment_id: '',
            reason: '',
        },
    })

    const selectedAssignmentId = watch('assignment_id')

    const selectedAssignment = useMemo(() => {
        return availableAssignments.find(
            (a: OncallAssignmentResponse) =>
                String(a.oncall_assignment_id) === selectedAssignmentId
        )
    }, [availableAssignments, selectedAssignmentId])

    const { mutateAsync: doReassign } = useMutationWithHandler(
        {
            mutationFn: reassignOncallAssignment,
        },
        {
            reactQueryKeys: QUERY_KEYS.ONCALL_ASSIGNMENTS,
            errorMessage: 'Failed to take on-call duty',
        }
    )

    const handleTakeOncall = async (formData: FormData) => {
        if (!employee?.employee_id) {
            toast.error(translate('user_not_found'))
            return
        }

        setIsProcessing(true)
        try {
            await doReassign({
                id: Number(formData.assignment_id),
                data: {
                    new_employee_id: employee.employee_id,
                    reassignment_reason:
                        formData.reason ||
                        translate('take_oncall_default_reason'),
                },
            })

            toast.success(translate('take_oncall_success'))
            onConfirm?.()
            reset()
            onClose()
        } catch (error) {
            console.error('Take on-call failed:', error)
        } finally {
            setIsProcessing(false)
        }
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    const hasNoAssignments = !isLoading && availableAssignments.length === 0

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={translate('take_oncall')}
            maxWidth="sm"
        >
            {isLoading ? (
                <div className="py-8 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : hasNoAssignments ? (
                <div className="py-8 text-center">
                    <Typogaphy size="medium" className="text-gray-500">
                        {translate('no_active_oncall_assignments')}
                    </Typogaphy>
                    <Typogaphy size="small" className="text-gray-400 mt-2">
                        {translate('no_oncall_to_take')}
                    </Typogaphy>
                    <div className="mt-6">
                        <Button variant="outlined" onClick={handleClose}>
                            {translate('close')}
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <Typogaphy size="small" className="text-gray-600 mb-4">
                        {translate('take_oncall_description')}
                    </Typogaphy>

                    <div className="space-y-4">
                        {/* Assignment selector */}
                        <FormItem
                            control={control}
                            name="assignment_id"
                            type="select"
                            label={`${translate('select_assignment_to_take')} *`}
                            placeholder={translate('select_assignment')}
                            options={assignmentOptions}
                        />

                        {/* Show current assignee info */}
                        {selectedAssignment && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <Typogaphy
                                    size="small"
                                    className="text-gray-600 font-medium mb-2"
                                >
                                    {translate('current_assignment_details')}
                                </Typogaphy>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-gray-500">
                                            {translate('schedule')}:
                                        </span>
                                        <span className="ml-2 font-medium">
                                            {
                                                selectedAssignment.oncall_schedule_name
                                            }
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">
                                            {translate('date')}:
                                        </span>
                                        <span className="ml-2 font-medium">
                                            {dayjs(
                                                selectedAssignment.oncall_date
                                            ).format('DD/MM/YYYY')}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">
                                            {translate('current_assignee')}:
                                        </span>
                                        <span className="ml-2 font-medium">
                                            {selectedAssignment.employee_name}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">
                                            {translate('shift_time')}:
                                        </span>
                                        <span className="ml-2 font-medium">
                                            {
                                                selectedAssignment.oncall_start_time
                                            }{' '}
                                            -{' '}
                                            {selectedAssignment.oncall_end_time}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Reason */}
                        <FormItem
                            control={control}
                            name="reason"
                            type="textarea"
                            label={translate('reason')}
                            placeholder={translate(
                                'take_oncall_reason_placeholder'
                            )}
                        />

                        {/* Warning */}
                        <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                            <Typogaphy size="small" className="text-amber-700">
                                {translate('take_oncall_warning')}
                            </Typogaphy>
                        </div>
                    </div>

                    <div className="flex justify-end gap-4 mt-6">
                        <Button variant="outlined" onClick={handleClose}>
                            {translate('cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit(handleTakeOncall)}
                            loading={isProcessing}
                            disabled={!selectedAssignmentId || isProcessing}
                        >
                            {translate('take_oncall')}
                        </Button>
                    </div>
                </>
            )}
        </Dialog>
    )
}

export default TakeOncallModal
